/**
 * Application Insights resource service.
 *
 * Queries Azure Resource Graph for the user's reachable Application Insights
 * (`microsoft.insights/components`) resources so the Observability picker can
 * present them as a list. The connection string is returned directly by the
 * Resource Graph query — no follow-up GET per resource is required.
 *
 * Per Microsoft documentation, the connection string is **not** a secret
 * (`Ikey unique identifiers aren't security tokens or security keys, and
 * aren't considered secrets.` —
 * https://learn.microsoft.com/en-us/azure/azure-monitor/app/sdk-connection-string).
 * Reader on the resource is sufficient.
 */

import { resourceGraphQuery, type AzureSubscription } from '@/services/azureResourceService';
import { isArmMockMode } from '@/services/armConfig';

export interface AppInsightsResource {
  id: string;
  name: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
  connectionString: string;
  /** True when the resource has at least one private link scoped resource attached. */
  hasPrivateEndpoint: boolean;
}

/**
 * Resource Graph row shape — the `project` clause in the KQL query below
 * controls what keys appear here.
 */
interface AppInsightsRow {
  id: string;
  name: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
  connectionString: string | null;
  privateLinkScopedResources: unknown[] | null;
}

const QUERY = `
Resources
| where type == "microsoft.insights/components"
| project id, name, location, resourceGroup, subscriptionId,
          connectionString = properties.ConnectionString,
          privateLinkScopedResources = properties.PrivateLinkScopedResources
| order by name asc
`;

/**
 * Hardcoded fixture used in mock-ARM mode so the picker works in
 * `npm run dev` and E2E suites without a real Azure backend. Mirrors the
 * pattern used by `fetchOttoApps` in `resourceGraph.ts`.
 */
const LOCAL_MOCK_RESOURCES: AppInsightsResource[] = [
  {
    id: '/subscriptions/local-subscription/resourceGroups/demo-rg/providers/microsoft.insights/components/otto-dev-ai',
    name: 'otto-dev-ai',
    location: 'eastus',
    resourceGroup: 'demo-rg',
    subscriptionId: 'local-subscription',
    connectionString:
      'InstrumentationKey=11111111-1111-1111-1111-111111111111;IngestionEndpoint=https://eastus-0.in.applicationinsights.azure.com/',
    hasPrivateEndpoint: false,
  },
  {
    id: '/subscriptions/local-subscription/resourceGroups/demo-rg/providers/microsoft.insights/components/otto-staging-ai',
    name: 'otto-staging-ai',
    location: 'westus2',
    resourceGroup: 'demo-rg',
    subscriptionId: 'local-subscription',
    connectionString:
      'InstrumentationKey=22222222-2222-2222-2222-222222222222;IngestionEndpoint=https://westus2-1.in.applicationinsights.azure.com/',
    hasPrivateEndpoint: false,
  },
  {
    id: '/subscriptions/local-subscription/resourceGroups/observability-rg/providers/microsoft.insights/components/otto-prod-ai-eastus',
    name: 'otto-prod-ai-eastus',
    location: 'eastus',
    resourceGroup: 'observability-rg',
    subscriptionId: 'local-subscription',
    connectionString:
      'InstrumentationKey=33333333-3333-3333-3333-333333333333;IngestionEndpoint=https://eastus-3.in.applicationinsights.azure.com/;LiveEndpoint=https://eastus.livediagnostics.monitor.azure.com/',
    hasPrivateEndpoint: true,
  },
];

/**
 * Fetch all Application Insights resources visible to the caller across the
 * supplied subscriptions. Returns an empty array when there are no results;
 * throws on transport / API errors (caller surfaces the error banner).
 */
export const getAppInsightsResources = async (
  accessToken: string,
  subscriptions: AzureSubscription[],
): Promise<AppInsightsResource[]> => {
  if (isArmMockMode()) {
    return LOCAL_MOCK_RESOURCES;
  }
  if (subscriptions.length === 0) {
    return [];
  }
  const rows = await resourceGraphQuery<AppInsightsRow>(
    accessToken,
    QUERY,
    subscriptions.map((s) => s.subscriptionId),
  );
  return rows
    .filter((r) => typeof r.connectionString === 'string' && r.connectionString.length > 0)
    .map((r) => ({
      id: r.id,
      name: r.name,
      location: r.location,
      resourceGroup: r.resourceGroup,
      subscriptionId: r.subscriptionId,
      connectionString: r.connectionString as string,
      hasPrivateEndpoint: Array.isArray(r.privateLinkScopedResources) && r.privateLinkScopedResources.length > 0,
    }));
};
